import { useState } from "react";
import axios from "axios";

/**
 * Rescue page — DB-independent emergency restart of the backend.
 *
 * Standalone page (NOT inside the AppShell, NOT using the auth context).
 * Why: when the backend is stuck because Mongo is dead, normal /api/auth/login
 * can't validate. This page hits /api/admin/rescue-login (env-var creds, no DB
 * touch) → gets a 10-minute restart-scoped JWT → POSTs /api/admin/restart.
 *
 * The rescue token can ONLY trigger restart — it cannot read candidates, jobs,
 * users, or any business data. Credentials live in backend env vars
 * (RESCUE_EMAIL / RESCUE_PASSWORD) and are documented in MONITORING_GUIDE.md.
 *
 * Visit: https://<your-domain>/rescue
 */
export default function Rescue() {
    const API = process.env.REACT_APP_BACKEND_URL;
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);
    const [step, setStep] = useState("login"); // login | confirm | done | error
    const [error, setError] = useState("");
    const [restartTs, setRestartTs] = useState("");
    const [tokenRef, setTokenRef] = useState("");

    const doLogin = async (e) => {
        e?.preventDefault();
        setBusy(true); setError("");
        try {
            const { data } = await axios.post(`${API}/api/admin/rescue-login`, { email, password });
            setTokenRef(data.access_token);
            setStep("confirm");
        } catch (err) {
            setError(err.response?.data?.detail || err.message || "Login failed");
            setStep("error");
        } finally { setBusy(false); }
    };

    const doRestart = async () => {
        setBusy(true); setError("");
        try {
            const { data } = await axios.post(
                `${API}/api/admin/restart`,
                {},
                { headers: { Authorization: `Bearer ${tokenRef}` }, withCredentials: false }
            );
            setRestartTs(data.ts || new Date().toISOString());
            setStep("done");
        } catch (err) {
            setError(err.response?.data?.detail || err.message || "Restart failed");
            setStep("error");
        } finally { setBusy(false); }
    };

    return (
        <div style={{ minHeight: "100vh", background: "#0b1220", color: "#e6ecff", fontFamily: "ui-monospace, SFMono-Regular, monospace", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
            <div style={{ maxWidth: 480, width: "100%" }}>
                <div style={{ borderLeft: "3px solid #fb923c", paddingLeft: 14, marginBottom: 28 }}>
                    <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".18em", color: "#fb923c" }}>Emergency rescue</div>
                    <h1 style={{ fontSize: 26, fontWeight: 900, margin: "6px 0 0 0", letterSpacing: "-0.01em", fontFamily: "Inter, sans-serif" }}>Restart backend</h1>
                    <p style={{ fontSize: 13, color: "#9aa6c0", marginTop: 8, lineHeight: 1.6, fontFamily: "Inter, sans-serif" }}>
                        Use this page only when the regular admin login fails (e.g. backend hung, /api/health returns 503).
                        Credentials validate without touching MongoDB. The issued token can <strong style={{ color: "#fff" }}>only restart the backend</strong> — nothing else.
                    </p>
                </div>

                {step === "login" && (
                    <form onSubmit={doLogin} data-testid="rescue-login-form">
                        <label style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: ".16em", color: "#9aa6c0" }}>Rescue email</label>
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="rescue@portalwiz.com"
                            required
                            autoFocus
                            style={{ width: "100%", height: 42, marginTop: 6, padding: "0 12px", background: "#111a2e", border: "1px solid #1f2a44", color: "#e6ecff", fontFamily: "inherit", fontSize: 14, outline: "none" }}
                            data-testid="rescue-email"
                        />
                        <label style={{ display: "block", marginTop: 16, fontSize: 10, textTransform: "uppercase", letterSpacing: ".16em", color: "#9aa6c0" }}>Rescue password</label>
                        <input
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            style={{ width: "100%", height: 42, marginTop: 6, padding: "0 12px", background: "#111a2e", border: "1px solid #1f2a44", color: "#e6ecff", fontFamily: "inherit", fontSize: 14, outline: "none" }}
                            data-testid="rescue-password"
                        />
                        <button
                            type="submit"
                            disabled={busy}
                            style={{ width: "100%", marginTop: 22, height: 44, background: "#fb923c", color: "#0b1220", border: "none", fontWeight: 800, fontSize: 13, textTransform: "uppercase", letterSpacing: ".14em", cursor: busy ? "not-allowed" : "pointer", opacity: busy ? 0.6 : 1, fontFamily: "inherit" }}
                            data-testid="rescue-submit"
                        >
                            {busy ? "Authenticating…" : "Authenticate →"}
                        </button>
                    </form>
                )}

                {step === "confirm" && (
                    <div data-testid="rescue-confirm">
                        <div style={{ background: "#111a2e", border: "1px solid #1f2a44", padding: 18, marginBottom: 18 }}>
                            <div style={{ fontSize: 11, color: "#9aa6c0", textTransform: "uppercase", letterSpacing: ".14em" }}>Token issued</div>
                            <div style={{ fontFamily: "Inter, sans-serif", fontSize: 14, color: "#e6ecff", marginTop: 6 }}>Valid for 10 minutes. Restart-scope only.</div>
                        </div>
                        <p style={{ fontSize: 13, color: "#e6ecff", lineHeight: 1.6, fontFamily: "Inter, sans-serif" }}>
                            Clicking <strong>Restart backend now</strong> will:
                        </p>
                        <ul style={{ fontSize: 13, color: "#9aa6c0", lineHeight: 1.7, fontFamily: "Inter, sans-serif", paddingLeft: 18 }}>
                            <li>Terminate the backend process in ~1 second</li>
                            <li>Trigger Kubernetes / supervisor to recreate it</li>
                            <li>Service is typically back in 10-20 seconds</li>
                            <li>Open API connections will be cut — recruiters may need to refresh</li>
                        </ul>
                        <button
                            onClick={doRestart}
                            disabled={busy}
                            style={{ width: "100%", marginTop: 18, height: 44, background: "#dc2626", color: "#fff", border: "none", fontWeight: 800, fontSize: 13, textTransform: "uppercase", letterSpacing: ".14em", cursor: busy ? "not-allowed" : "pointer", opacity: busy ? 0.6 : 1, fontFamily: "inherit" }}
                            data-testid="rescue-restart-btn"
                        >
                            {busy ? "Sending restart signal…" : "Restart backend now →"}
                        </button>
                        <button
                            onClick={() => { setStep("login"); setTokenRef(""); setPassword(""); }}
                            disabled={busy}
                            style={{ width: "100%", marginTop: 8, height: 36, background: "transparent", color: "#9aa6c0", border: "1px solid #1f2a44", fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}
                            data-testid="rescue-cancel"
                        >
                            Cancel
                        </button>
                    </div>
                )}

                {step === "done" && (
                    <div style={{ background: "#111a2e", border: "1px solid #16a34a", padding: 20 }} data-testid="rescue-done">
                        <div style={{ fontSize: 11, color: "#16a34a", textTransform: "uppercase", letterSpacing: ".14em" }}>Restart signal sent</div>
                        <h2 style={{ fontSize: 18, fontWeight: 800, fontFamily: "Inter, sans-serif", marginTop: 8, marginBottom: 8 }}>Backend is restarting</h2>
                        <p style={{ fontSize: 13, color: "#9aa6c0", lineHeight: 1.6, fontFamily: "Inter, sans-serif", margin: 0 }}>
                            Restart triggered at {restartTs}. Give it ~15 seconds, then verify with:
                        </p>
                        <pre style={{ background: "#0b1220", padding: 10, fontSize: 12, color: "#cbd5e1", marginTop: 12, overflow: "auto" }}>curl {API}/api/health</pre>
                        <a
                            href="/login"
                            style={{ display: "inline-block", marginTop: 14, fontSize: 12, color: "#fb923c", textDecoration: "underline", fontFamily: "Inter, sans-serif" }}
                            data-testid="rescue-back-login"
                        >← Back to normal login</a>
                    </div>
                )}

                {step === "error" && (
                    <div style={{ background: "#1c1010", border: "1px solid #dc2626", padding: 18 }} data-testid="rescue-error">
                        <div style={{ fontSize: 11, color: "#fca5a5", textTransform: "uppercase", letterSpacing: ".14em" }}>Error</div>
                        <p style={{ fontSize: 13, color: "#fecaca", lineHeight: 1.6, fontFamily: "Inter, sans-serif", margin: "8px 0 16px 0" }}>{error}</p>
                        <button
                            onClick={() => { setStep("login"); setError(""); setTokenRef(""); }}
                            style={{ height: 36, padding: "0 16px", background: "transparent", color: "#fb923c", border: "1px solid #fb923c", fontSize: 12, cursor: "pointer", fontFamily: "inherit", textTransform: "uppercase", letterSpacing: ".12em" }}
                            data-testid="rescue-retry"
                        >Try again</button>
                    </div>
                )}

                <div style={{ marginTop: 32, fontSize: 11, color: "#475569", textAlign: "center", fontFamily: "Inter, sans-serif" }}>
                    Portalwiz Talent Solutions · Restricted rescue access
                </div>
            </div>
        </div>
    );
}
